import React, { useState, useEffect } from 'react';
import CarCard from './CarCard';
import API_BASE_URL from '../../config/api';
import './CarList.css';

const CarList = ({ filters = {} }) => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const carsPerPage = 12;

  // Fetch cars from API
  const fetchCars = async (filterParams = {}, page = 1) => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: page,
        limit: carsPerPage,
        ...filterParams
      });

      const response = await fetch(`${API_BASE_URL}/api/cars?${queryParams}`);
      
      if (!response.ok) {
        throw new Error('Eroare la încărcarea mașinilor');
      }

      const data = await response.json();
      
      if (data.success) {
        setCars(data.data);
        setTotalPages(data.pagination.pages);
        setCurrentPage(data.pagination.page);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching cars:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars(filters, 1);
  }, [filters]);

  const handlePageChange = (page) => {
    fetchCars(filters, page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="pagination-btn"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="dots1" className="pagination-dots">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="dots2" className="pagination-dots">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="pagination-btn"
        >
          {totalPages}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn pagination-nav"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" 
                  strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Anterior
        </button>

        {pages}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn pagination-nav"
        >
          Următor
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" 
                  strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="car-list-loading">
        <div className="spinner"></div>
        <p>Se încarcă mașinile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="car-list-error">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" 
                strokeLinecap="round"/>
        </svg>
        <h3>Eroare</h3>
        <p>{error}</p>
        <button onClick={() => fetchCars(filters)} className="retry-btn">
          Încearcă din nou
        </button>
      </div>
    );
  }

  if (cars.length === 0) {
    return (
      <div className="car-list-empty">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" 
                stroke="currentColor" strokeWidth="2"/>
          <path d="M12 12h.01M12 16h.01" stroke="currentColor" strokeWidth="2" 
                strokeLinecap="round"/>
        </svg>
        <h3>Nicio mașină găsită</h3>
        <p>Încearcă să modifici filtrele de căutare</p>
      </div>
    );
  }

  return (
    <div className="car-list-section">
      <div className="car-list-grid">
        {cars.map(car => (
          <CarCard key={car._id} car={car} />
        ))}
      </div>

      {renderPagination()}
    </div>
  );
};

export default CarList;